export const MAX_CONCURRENT_UPLOADS = 10;
export const PROGRESS_THROTTLE_MS = 100;

const FULL_PERCENT = 100;

export const createAbortError = () => {
  const error = new Error('Upload aborted');
  error.name = 'AbortError';
  return error;
};

export const isAbortError = (error) => error?.name === 'AbortError';

export const getPartBlob = (file, partSize, partNumber) =>
  file.slice((partNumber - 1) * partSize, partNumber * partSize);

export const getPartByteLength = (fileSize, partSize, partNumber) =>
  Math.max(0, Math.min(partSize, fileSize - (partNumber - 1) * partSize));

export const getUploadPercent = (uploadedBytes, totalBytes) => {
  if (totalBytes === 0) {
    return FULL_PERCENT;
  }

  return Math.min(
    FULL_PERCENT,
    Math.floor((uploadedBytes / totalBytes) * FULL_PERCENT)
  );
};

export const createSlotLimiter = (limit) => {
  let active = 0;
  const queue = [];

  let pump;

  const createRelease = () => {
    let released = false;

    return () => {
      if (!released) {
        released = true;
        active -= 1;
        pump();
      }
    };
  };

  pump = () => {
    while (active < limit && queue.length > 0) {
      const waiter = queue.shift();
      active += 1;
      waiter.grant(createRelease());
    }
  };

  const acquire = (signal) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(createAbortError());
        return;
      }

      const waiter = {};
      waiter.cancel = () => {
        const index = queue.indexOf(waiter);
        if (index >= 0) {
          queue.splice(index, 1);
        }
        reject(createAbortError());
      };
      waiter.grant = (release) => {
        signal?.removeEventListener('abort', waiter.cancel);
        resolve(release);
      };
      signal?.addEventListener('abort', waiter.cancel, { once: true });
      queue.push(waiter);
      pump();
    });

  return { acquire, getActiveCount: () => active };
};

export const uploadSlots = createSlotLimiter(MAX_CONCURRENT_UPLOADS);

export const createThrottledReporter = (
  report,
  intervalMs = PROGRESS_THROTTLE_MS
) => {
  let timer = null;
  let lastReportAt = null;
  let pending = null;

  const emitPending = () => {
    const value = pending;
    pending = null;
    timer = null;
    lastReportAt = Date.now();
    report(value);
  };

  const push = (value) => {
    pending = value;

    if (timer !== null) {
      return;
    }

    const elapsed =
      lastReportAt === null ? intervalMs : Date.now() - lastReportAt;

    if (elapsed >= intervalMs) {
      emitPending();
      return;
    }

    timer = setTimeout(emitPending, intervalMs - elapsed);
  };

  const flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }

    if (pending !== null) {
      emitPending();
    }
  };

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }

    pending = null;
  };

  return { push, flush, cancel };
};

export const MULTIPART_MAX_ATTEMPTS = 3;
export const MULTIPART_RETRY_BASE_DELAY_MS = 500;

const RETRYABLE_CLIENT_STATUSES = [408, 429];
const SERVER_ERROR_STATUS = 500;

const isRetryableError = (error) => {
  if (isAbortError(error)) {
    return false;
  }

  if (error?.status == null) {
    return true;
  }

  return (
    error.status >= SERVER_ERROR_STATUS ||
    RETRYABLE_CLIENT_STATUSES.includes(error.status)
  );
};

const defaultSleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const waiter = {};
    waiter.onAbort = () => {
      clearTimeout(waiter.timer);
      reject(createAbortError());
    };
    waiter.timer = setTimeout(() => {
      signal?.removeEventListener('abort', waiter.onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', waiter.onAbort, { once: true });
  });

const uploadPartWithRetry = async ({
  url,
  body,
  putPart,
  signal,
  maxAttempts,
  retryBaseDelayMs,
  sleep,
  onBytes,
}) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      // A part is retried one attempt after another.
      // eslint-disable-next-line no-await-in-loop
      await putPart(url, body, { signal, onUploadProgress: onBytes });
      return;
    } catch (error) {
      onBytes(0);

      if (attempt >= maxAttempts || !isRetryableError(error)) {
        throw error;
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(retryBaseDelayMs * 2 ** (attempt - 1), signal);
    }
  }
};

export async function uploadParts({
  file,
  partSize,
  partUploadUrls,
  uploadedParts = [],
  putPart,
  limiter = uploadSlots,
  maxAttempts = MULTIPART_MAX_ATTEMPTS,
  retryBaseDelayMs = MULTIPART_RETRY_BASE_DELAY_MS,
  progressIntervalMs = PROGRESS_THROTTLE_MS,
  sleep = defaultSleep,
  signal,
  onPartUploaded = () => {},
  onProgress = () => {},
}) {
  const totalBytes = file.size;
  const finished = new Set(uploadedParts);
  const inFlightBytes = new Map();
  const lengthOf = (partNumber) =>
    getPartByteLength(totalBytes, partSize, partNumber);
  let completedBytes = [...finished].reduce(
    (sum, partNumber) => sum + lengthOf(partNumber),
    0
  );
  const isDone = () => finished.size === partUploadUrls.length;
  const reporter = createThrottledReporter(onProgress, progressIntervalMs);

  const publishProgress = () => {
    if (isDone()) {
      reporter.push({ uploadedBytes: totalBytes, totalBytes });
      return;
    }

    if (totalBytes === 0) {
      return;
    }

    const inFlight = [...inFlightBytes.values()].reduce(
      (sum, bytes) => sum + bytes,
      0
    );
    reporter.push({
      uploadedBytes: Math.min(completedBytes + inFlight, totalBytes - 1),
      totalBytes,
    });
  };

  const pendingParts = [];
  for (
    let partNumber = 1;
    partNumber <= partUploadUrls.length;
    partNumber += 1
  ) {
    if (!finished.has(partNumber)) {
      pendingParts.push(partNumber);
    }
  }

  const pool = new AbortController();
  const abortPool = () => pool.abort();
  let firstError = null;

  if (signal?.aborted) {
    abortPool();
  } else {
    signal?.addEventListener('abort', abortPool, { once: true });
  }

  const runPart = async (partNumber) => {
    const release = await limiter.acquire(pool.signal);

    try {
      inFlightBytes.set(partNumber, 0);
      publishProgress();
      await uploadPartWithRetry({
        url: partUploadUrls[partNumber - 1],
        body: getPartBlob(file, partSize, partNumber),
        putPart,
        signal: pool.signal,
        maxAttempts,
        retryBaseDelayMs,
        sleep,
        onBytes: (loadedBytes) => {
          if (inFlightBytes.has(partNumber)) {
            inFlightBytes.set(
              partNumber,
              Math.min(loadedBytes, lengthOf(partNumber))
            );
            publishProgress();
          }
        },
      });
    } catch (error) {
      firstError = firstError ?? error;
      pool.abort();
      throw error;
    } finally {
      inFlightBytes.delete(partNumber);
      release();
    }

    finished.add(partNumber);
    completedBytes += lengthOf(partNumber);
    onPartUploaded(partNumber);
    publishProgress();
  };

  publishProgress();

  await Promise.all(
    pendingParts.map((partNumber) =>
      runPart(partNumber).catch((error) => {
        firstError = firstError ?? error;
        pool.abort();
      })
    )
  );

  signal?.removeEventListener('abort', abortPool);

  if (firstError) {
    reporter.cancel();
    throw firstError;
  }

  reporter.flush();

  return [...finished].sort((a, b) => a - b);
}
